// @ts-strict-ignore
import { useEffect, useState } from 'react';
import { debounce } from 'lodash';
import { captureException } from '@sentry/nextjs';
import { useSelector } from 'src/store/store';
import { MAX_MENTION_SUGGESTIONS } from 'src/constants/write';
import api from 'src/utils/api';
import useDebouncedState from 'src/utils/hooks/useDebouncedState';

export type Mention = {
  username: string;
  userId: number;
  imageUrl?: string;
  userImageHash?: string;
}
type Return = {
  suggestedMentions: Mention[];
  searchText?: string;
  setSearchText: (searchText: string) => void;
}
export function useFetchCommunityMentions(): Return {
  const { currentCommunity, currentUser } = useSelector((state) => state.context);
  const [searchText, setSearchText] = useState<string>('');
  const [suggestedMentions, setSuggestedMentions] = useState([]);

  useEffect(() => {
    async function fetchMentionSuggestions() {
      try {
        const { data: members } = await api.get('private/search/members/by-name', {
          params: {
            q: searchText?.toLowerCase() || '',
            communityId: currentCommunity?.communityId,
            limit: MAX_MENTION_SUGGESTIONS + suggestedMentions.length,
          },
        });

        const users = members
          .filter(({ userId }) => !suggestedMentions.includes(userId) && userId !== currentUser.userId)
          .slice(0, 4);
        setSuggestedMentions(users);

      } catch (ex) {
        captureException(ex);
      }
    }
    const reasonableFetch = debounce(() => fetchMentionSuggestions(), 200);

    if (searchText?.length < 2) {
      setSuggestedMentions([]);
    } else {
      reasonableFetch();
    }

  }, [searchText]);

  return { suggestedMentions, setSearchText, searchText };
}

export function useFetchMentions(): Return {
  const currentUserId = useSelector((state) => state.context.currentUser?.userId);
  const [searchText, setSearchText] = useDebouncedState('');
  const [suggestedMentions, setSuggestedMentions] = useState([]);

  async function fetchMentionSuggestions(searchText) {
    if (searchText.length < 2) return setSuggestedMentions([]);
    try {
      const { data: members } = await api.get('private/search/members/by-name', {
        params: {
          limit: 10,
          q: searchText,
        },
      });
      setSuggestedMentions(members.filter(({ userId }) => userId !== currentUserId));
    } catch (ex) {
      captureException(ex);
    }
  }

  useEffect(() => {
    fetchMentionSuggestions(searchText);
  }, [searchText]);

  return { suggestedMentions, setSearchText, searchText };
}
